import type { Column } from '@shared/types/data-table'
import type { MmDeposit } from '@features/money-movement/types/mm-deposit.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import { createStatusColumn, createDateColumn, createTextColumn, createCurrencyColumn } from './column-factories'

const methodLabels: Record<string, string> = {
  wire: 'Wire',
  ach: 'ACH',
  sepa: 'SEPA',
  internal: 'Internal',
}

export const MM_DEPOSIT_COLUMNS: Record<string, Column<MmDeposit>> = {
  merchantName: createTextColumn<MmDeposit>('merchantName', 'Merchant', 200),
  status: createStatusColumn<MmDeposit>('mm-deposit'),
  method: createTextColumn<MmDeposit>(
    'method', 'Method', 100, undefined, undefined,
    (value: string) => methodLabels[value] || value
  ),
  currency: createTextColumn<MmDeposit>('currency', 'Currency', 90),
  amount: createCurrencyColumn<MmDeposit>('amount', 'Amount', 140),
  sourceAccount: createTextColumn<MmDeposit>('sourceAccount', 'Source', 180),
  destinationAccount: createTextColumn<MmDeposit>('destinationAccount', 'Destination', 180),
  reason: createTextColumn<MmDeposit>('reason', 'Reason', 220),
  reference: createTextColumn<MmDeposit>('reference', 'Reference', 180),
  initiatedBy: createTextColumn<MmDeposit>('initiatedBy', 'Initiated By', 120),
  createdAt: createDateColumn<MmDeposit>('createdAt', 'Created', 150),
  completedAt: createDateColumn<MmDeposit>('completedAt', 'Completed', 150),
}

export const MM_DEPOSIT_STATUS_CONFIG = {
  all: ['merchantName', 'status', 'method', 'currency', 'amount', 'reason', 'reference', 'initiatedBy', 'createdAt'],
  completed: ['merchantName', 'status', 'method', 'currency', 'amount', 'reason', 'completedAt'],
  pending: ['merchantName', 'status', 'method', 'currency', 'amount', 'reason', 'createdAt'],
  'in-transit': ['merchantName', 'status', 'method', 'currency', 'amount', 'reason', 'reference'],
  failed: ['merchantName', 'status', 'method', 'currency', 'amount', 'reason', 'createdAt'],
} as const

export const getMmDepositColumns = (status: string = 'all'): Column<MmDeposit>[] => {
  const keys = MM_DEPOSIT_STATUS_CONFIG[status as keyof typeof MM_DEPOSIT_STATUS_CONFIG] || MM_DEPOSIT_STATUS_CONFIG.all
  return keys.map(key => MM_DEPOSIT_COLUMNS[key]).filter(Boolean)
}

export const getMmDepositColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(MM_DEPOSIT_STATUS_CONFIG[status as keyof typeof MM_DEPOSIT_STATUS_CONFIG] || MM_DEPOSIT_STATUS_CONFIG.all)
  return Object.entries(MM_DEPOSIT_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['merchantName', 'status'].includes(key),
    order: index
  }))
}
